import { useId, useRef, type CSSProperties, type ReactNode, type RefObject } from "react";
import { useFocusTrap } from "./focusTrap.js";

/**
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 — generic
 * centred modal dialog.
 *
 * Before this primitive the app had three incompatible overlay shapes:
 * a real focus-trapped side `Drawer`, a hand-rolled `position: fixed`
 * card (`AssignDeviceModal`), and — worst — `DeleteWithConfirm`, which
 * announced `role="dialog" aria-modal="true"` while rendering inline in
 * its trigger's layout slot with no scrim, no trap, no Escape and no
 * focus return. `aria-modal="true"` on a non-modal node actively lies to
 * assistive technology.
 *
 * `Modal` is the honest centred counterpart to `Drawer`: same focus
 * discipline (shared via `focusTrap.ts`), different presentation. A
 * side drawer is for a bounded *editing* workflow; a modal is for a
 * short, blocking *decision*. They are not interchangeable, which is
 * why this exists rather than reusing `Drawer` for confirmations.
 *
 * Accessibility contract:
 *   - `role="dialog"` + `aria-modal="true"` on a real overlay above a scrim.
 *   - Accessible name via `ariaLabel` or the rendered `title`.
 *   - Optional accessible description wired through `aria-describedby`.
 *   - Initial focus moves into the panel (or to `initialFocusRef`).
 *   - Tab cycles inside the panel; Escape closes when `dismissible`.
 *   - Scrim click closes when `dismissible`.
 *   - On close, focus returns to the element that opened it.
 *   - Renders nothing when `open` is false.
 */
interface ModalOwnProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Id of the element describing the dialog (wired to `aria-describedby`). */
  readonly describedBy?: string;
  /**
   * When false, Escape and scrim clicks do not close the dialog — used
   * while a destructive action is in flight. Defaults to true.
   */
  readonly dismissible?: boolean;
  /**
   * Panel max inline size. Defaults to the
   * `--gt-cmp-modal-max-inline-size` component token.
   */
  readonly width?: number | string;
  /** Element to focus on open. Defaults to the first focusable child. */
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  readonly children: ReactNode;
  readonly dataTestId?: string;
}

/**
 * The accessible-name contract, enforced by the type system.
 *
 * A `role="dialog"` with no accessible name is announced as an anonymous
 * "dialog" and is a WCAG 4.1.2 failure. Making both `title` and
 * `ariaLabel` optional let a caller create exactly that, so the two are
 * now a discriminated union: **a Modal cannot be constructed without a
 * name.** A runtime warning was rejected as insufficient — it fires only
 * once the broken dialog is already in front of a user.
 *
 * - Branch 1 — a rendered `title` supplies the name via `aria-labelledby`.
 *   `ariaLabel` is forbidden here so there is exactly one source of truth.
 * - Branch 2 — no rendered heading, so `ariaLabel` is required and the
 *   name is supplied via `aria-label`.
 */
export type ModalNameProps =
  | { readonly title: ReactNode; readonly ariaLabel?: never }
  | { readonly title?: undefined; readonly ariaLabel: string };

export type ModalProps = ModalOwnProps & ModalNameProps;

export function Modal(props: ModalProps) {
  const {
    open,
    onClose,
    describedBy,
    dismissible = true,
    width = "var(--gt-cmp-modal-max-inline-size)",
    initialFocusRef,
    children,
    dataTestId,
  } = props;
  /*
    Explicit presence check, not a truthiness check: a legitimate title
    can be falsy (`0`, or an element that renders nothing visible), and
    `title ? … : …` would silently drop the name for those.
  */
  const hasTitle = props.title !== undefined;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const headingId = useId();

  const { onKeyDown } = useFocusTrap({
    open,
    containerRef: panelRef,
    onClose,
    dismissOnEscape: dismissible,
    ...(initialFocusRef ? { initialFocusRef } : {}),
  });

  if (!open) return null;

  const panelStyle: CSSProperties = {
    position: "relative",
    inlineSize: "100%",
    maxInlineSize: width,
    maxBlockSize: "calc(100dvh - var(--gt-sys-space-8))",
    overflowY: "auto",
    background: "var(--gt-sys-surface-overlay, var(--gt-sys-surface))",
    color: "var(--gt-sys-text)",
    border: "1px solid var(--gt-sys-border)",
    borderRadius: "var(--gt-sys-radius-lg)",
    boxShadow: "var(--gt-sys-shadow-lg)",
    padding: "var(--gt-sys-space-5)",
    outline: "none",
    zIndex: "var(--gt-cmp-modal-panel-z)",
  };

  /*
    Exactly one accessible-name attribute is emitted, and it is never
    `undefined`: the union above guarantees that the branch without a
    rendered title carries an `ariaLabel`.
  */
  const nameAttrs = hasTitle ? { "aria-labelledby": headingId } : { "aria-label": props.ariaLabel };

  return (
    <>
      {/* Scrim. `aria-hidden` + click-to-close matches the Drawer pattern. */}
      <div
        aria-hidden="true"
        data-testid={dataTestId ? `${dataTestId}-scrim` : undefined}
        onClick={dismissible ? onClose : undefined}
        style={{
          position: "fixed",
          inset: 0,
          background: "var(--gt-cmp-modal-scrim)",
          zIndex: "var(--gt-cmp-modal-scrim-z)",
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--gt-sys-space-4)",
          zIndex: "var(--gt-cmp-modal-panel-z)",
          pointerEvents: "none",
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          {...nameAttrs}
          {...(describedBy ? { "aria-describedby": describedBy } : {})}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          data-testid={dataTestId}
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          {hasTitle ? (
            <h2
              id={headingId}
              style={{
                margin: 0,
                marginBlockEnd: "var(--gt-sys-space-3)",
                fontSize: "var(--gt-sys-font-size-lg)",
                fontWeight: "var(--gt-sys-font-weight-semibold)",
                lineHeight: "var(--gt-sys-line-height-tight)",
              }}
            >
              {props.title}
            </h2>
          ) : null}
          {children}
        </div>
      </div>
    </>
  );
}
